import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, getUser, isConfigured } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [configError, setConfigError] = useState(null);

    useEffect(() => {
        // Check if Supabase is configured
        if (!isConfigured) {
            console.error('Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
            setConfigError('Authentication service not configured');
            setLoading(false);
            return;
        }

        // Get initial session
        const initSession = async () => {
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                setSession(currentSession);

                if (currentSession?.user) {
                    setUser(currentSession.user);
                }
            } catch (error) {
                console.error('Error getting initial session:', error);
            } finally {
                setLoading(false);
            }
        };

        initSession();

        // Listen for auth changes - this is the primary source of truth
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, currentSession) => {
                console.log('Auth state changed:', event);
                setSession(currentSession);
                setUser(currentSession?.user || null);
                setLoading(false);
            }
        );

        return () => {
            subscription?.unsubscribe();
        };
    }, []);

    const signUp = async (email, password) => {
        if (!isConfigured) {
            return { data: null, error: { message: 'Authentication service not configured' } };
        }
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
        });
        return { data, error };
    };

    const signIn = async (email, password) => {
        if (!isConfigured) {
            return { data: null, error: { message: 'Authentication service not configured' } };
        }
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        return { data, error };
    };

    const signOut = async () => {
        if (!isConfigured) {
            setUser(null);
            setSession(null);
            return { error: null };
        }
        const { error } = await supabase.auth.signOut();
        if (!error) {
            setUser(null);
            setSession(null);
        }
        return { error };
    };

    const resetPassword = async (email) => {
        if (!isConfigured) {
            return { data: null, error: { message: 'Authentication service not configured' } };
        }
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        return { data, error };
    };

    const updatePassword = async (newPassword) => {
        if (!isConfigured) {
            return { data: null, error: { message: 'Authentication service not configured' } };
        }
        const { data, error } = await supabase.auth.updateUser({
            password: newPassword,
        });
        return { data, error };
    };

    const value = {
        user,
        session,
        loading,
        configError,
        signUp,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
        isAuthenticated: !!session,
        isConfigured,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
